import React, {
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { useSidebarOverlayStore } from "stores/useSidebarOverlayStore";

export interface DropdownItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onClick: () => void;
  selected?: boolean;
  subItems?: DropdownItem[];
  /// Indent for a row of a tree, 0 at its root.
  depth?: number;
}

interface SidebarDropdownProps {
  items: DropdownItem[];
  isVisible: boolean;
  topPosition?: number;
  sidebarWidth: string;
  headerContent?: React.ReactNode;
  /** Caps the width; past it a deep tree scrolls sideways inside the panel. */
  maxWidth?: string;
  /** Called when hover state changes across main dropdown + sub-menus. */
  onHoverChange?: (isHovered: boolean) => void;
}

export const SidebarDropdown: React.FC<SidebarDropdownProps> = ({
  items,
  isVisible,
  topPosition = 0,
  sidebarWidth,
  headerContent,
  maxWidth,
  onHoverChange,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const subMenuRef = useRef<HTMLDivElement>(null);
  const pushOverlay = useSidebarOverlayStore((s) => s.push);
  const popOverlay = useSidebarOverlayStore((s) => s.pop);
  const [adjustedTop, setAdjustedTop] = useState(topPosition);
  const [activeSubMenu, setActiveSubMenu] = useState<{
    id: string;
    items: DropdownItem[];
    top: number;
    left: number;
  } | null>(null);
  const subMenuTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useLayoutEffect(() => {
    if (!isVisible || !ref.current) {
      setAdjustedTop(topPosition);
      return;
    }
    const height = ref.current.offsetHeight;
    const viewportHeight = window.innerHeight;
    const margin = 8;

    if (topPosition + height + margin > viewportHeight) {
      setAdjustedTop(Math.max(margin, viewportHeight - height - margin));
    } else {
      setAdjustedTop(topPosition);
    }
  }, [isVisible, topPosition, items.length, headerContent]);

  // Reposition sub-menu if it overflows the viewport
  useLayoutEffect(() => {
    if (!activeSubMenu || !subMenuRef.current || !ref.current) return;
    const el = subMenuRef.current;
    const subRect = el.getBoundingClientRect();
    const mainRect = ref.current.getBoundingClientRect();
    const margin = 8;

    if (subRect.right + margin > window.innerWidth) {
      el.style.left = `${mainRect.left - subRect.width - margin}px`;
    }
    if (subRect.bottom + margin > window.innerHeight) {
      el.style.top = `${Math.max(margin, window.innerHeight - subRect.height - margin)}px`;
    }
  }, [activeSubMenu]);

  useEffect(() => {
    if (!isVisible) {
      setActiveSubMenu(null);
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    pushOverlay();
    return () => popOverlay();
  }, [isVisible, pushOverlay, popOverlay]);

  useEffect(() => {
    return () => {
      if (subMenuTimeoutRef.current) clearTimeout(subMenuTimeoutRef.current);
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const setHovered = useCallback(
    (hovered: boolean) => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (hovered) {
        onHoverChange?.(true);
      } else {
        hoverTimeoutRef.current = setTimeout(() => onHoverChange?.(false), 150);
      }
    },
    [onHoverChange],
  );

  const handleMainEnter = () => setHovered(true);

  const handleMainLeave = () => setHovered(false);

  const handleItemMouseEnter = (
    item: DropdownItem,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (subMenuTimeoutRef.current) clearTimeout(subMenuTimeoutRef.current);
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    if (item.subItems?.length) {
      const buttonRect = e.currentTarget.getBoundingClientRect();
      const mainRect = ref.current?.getBoundingClientRect();
      setActiveSubMenu({
        id: item.id,
        items: item.subItems,
        top: buttonRect.top,
        left: (mainRect?.right ?? buttonRect.right) + 8,
      });
    } else {
      subMenuTimeoutRef.current = setTimeout(() => setActiveSubMenu(null), 150);
    }
  };

  const handleSubMenuEnter = () => {
    if (subMenuTimeoutRef.current) clearTimeout(subMenuTimeoutRef.current);
    setHovered(true);
  };

  const handleSubMenuLeave = () => {
    subMenuTimeoutRef.current = setTimeout(() => setActiveSubMenu(null), 150);
    setHovered(false);
  };

  if (!isVisible || (!items.length && !headerContent)) return null;

  // On the body: inside the rail, a sticky or contained ancestor would trap
  // the panel's z-index and let the page beside the rail paint over it.
  return createPortal(
    <>
      <div
        ref={ref}
        className="sidebar-dropdown scrollbar-overlay-hover"
        style={{
          top: adjustedTop,
          left: `calc(${sidebarWidth} + 8px)`,
          maxWidth,
          maxHeight: "calc(100vh - 2 * var(--spacing-sm))",
          overflow: "auto",
        }}
        onMouseEnter={handleMainEnter}
        onMouseLeave={handleMainLeave}
      >
        {headerContent}
        {/* Every row as wide as the widest, so a scrolled tree's fills line up. */}
        <div className="w-max min-w-full">
          {items.map((item) => (
            <button
              key={item.id}
              onClick={item.subItems?.length ? undefined : item.onClick}
              onMouseEnter={(e) => handleItemMouseEnter(item, e)}
              aria-current={item.selected ? "page" : undefined}
              className={`w-full flex items-center px-3 py-2 hover:bg-list-hover text-left${activeSubMenu?.id === item.id ? " bg-list-hover" : item.selected ? " bg-list-selected-light" : ""}`}
              style={
                item.depth
                  ? {
                      paddingLeft: `calc(var(--spacing-md) * ${item.depth + 1})`,
                    }
                  : undefined
              }
            >
              {item.icon && (
                <div className="mr-2 flex items-center">{item.icon}</div>
              )}
              <span
                className={`${item.selected ? "text-fg-semibold" : "text-fg-muted-regular"} max-w-40 truncate grow`}
              >
                {item.label}
              </span>
              {item.rightIcon && (
                <div className="ml-2 flex items-center text-fg-muted">
                  {item.rightIcon}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
      {activeSubMenu && (
        <div
          ref={subMenuRef}
          className="sidebar-dropdown"
          style={{
            top: activeSubMenu.top,
            left: activeSubMenu.left,
          }}
          onMouseEnter={handleSubMenuEnter}
          onMouseLeave={handleSubMenuLeave}
        >
          {activeSubMenu.items.map((subItem) => (
            <button
              key={subItem.id}
              onClick={() => {
                subItem.onClick();
                setActiveSubMenu(null);
              }}
              className="w-full flex items-center px-3 py-2 hover:bg-list-hover text-left"
            >
              {subItem.icon && (
                <div className="mr-2 flex items-center">{subItem.icon}</div>
              )}
              <span
                className={`${subItem.selected ? "text-fg-semibold" : "text-fg-muted-regular"} truncate grow`}
              >
                {subItem.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </>,
    document.body,
  );
};
